import { useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  LoadingBlock,
  Mono,
  PageHeader,
  StatusBadge,
  Select,
  Table,
  Tag,
  Td,
  Th,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { formatCount, formatMoney } from "../../lib/money";
import {
  useAdminAffiliates,
  useMe,
  useSetAffiliateRole,
  useSetAffiliateStatus,
} from "../../lib/queries";
import type { AdminAffiliate, AffiliateRole } from "../../lib/types";
import { useTranslation } from "react-i18next";

export function AdminAffiliatesPage() {
  const { t } = useTranslation("admin");
  return (
    <>
      <PageHeader
        title={t("affiliates.title")}
        description={t("affiliates.description")}
      />
      <Affiliates />
    </>
  );
}

/** The people in the programme, and the two things an administrator changes. */
function Affiliates() {
  const { t } = useTranslation("admin");
  const me = useMe();
  const role = me.data?.affiliate.role;
  const [search, setSearch] = useState("");
  const affiliates = useAdminAffiliates(search);
  const setStatus = useSetAffiliateStatus();
  const setRole = useSetAffiliateRole();
  const [error, setError] = useState<string | null>(null);

  async function act(run: () => Promise<unknown>) {
    setError(null);
    try {
      await run();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("affiliates.failed"));
    }
  }

  return (
    <Card>
      <CardHeader
        title={t("affiliates.inProgramme")}
        action={
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("affiliates.searchPlaceholder")}
            className="h-8 w-56"
            maxLength={80}
          />
        }
      />
      {error && (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      )}
      {affiliates.isPending ? (
        <LoadingBlock />
      ) : affiliates.error ? (
        <div className="px-4 py-4">
          <Alert>{affiliates.error.message}</Alert>
        </div>
      ) : affiliates.data.length === 0 ? (
        <EmptyState
          title={search ? t("affiliates.noMatch") : t("affiliates.empty")}
          description={search ? t("affiliates.searchHint") : undefined}
        />
      ) : (
        <Table
          head={
            <tr>
              <Th>{t("affiliates.th.affiliate")}</Th>
              <Th priority="lg" className="text-right">
                {t("affiliates.th.links")}
              </Th>
              <Th priority="md" className="text-right">
                {t("affiliates.th.converted")}
              </Th>
              <Th className="text-right">{t("affiliates.th.earned")}</Th>
              {/* Dropped rather than left empty for a finance holder, who sees
                  this list read-only: a header with nothing under it reads as a
                  column that failed to load. */}
              {role === "admin" && <Th className="text-right" />}
            </tr>
          }
        >
          {affiliates.data.map((row) => (
            <AffiliateRow
              key={row.id}
              row={row}
              /*
               * Not on your own row. `setAffiliateRole` refuses a self-change
               * and `setAffiliateStatus` refuses a self-pause — both so an
               * administrator cannot lock the programme's last admin out — so
               * offering either here is offering a control that answers 403.
               */
              canManage={role === "admin" && row.id !== me.data?.affiliate.id}
              busy={setStatus.isPending || setRole.isPending}
              onStatus={(status, reason) =>
                void act(() => setStatus.mutateAsync({ id: row.id, status, reason }))
              }
              onRole={(role) => void act(() => setRole.mutateAsync({ id: row.id, role }))}
            />
          ))}
        </Table>
      )}
    </Card>
  );
}

function AffiliateRow({
  row,
  busy,
  canManage,
  onStatus,
  onRole,
}: {
  row: AdminAffiliate;
  busy: boolean;
  onStatus: (status: string, reason?: string) => void;
  onRole: (role: AffiliateRole) => void;
  /* Without it the row renders read-only, which is what a finance holder sees:
     they open affiliates to review traffic before releasing a payout, and
     pausing somebody or handing out a role is not their decision to make. */
  canManage: boolean;
}) {
  const { t } = useTranslation("admin");
  const paused = row.status === "paused" || row.status === "banned";

  return (
    <tr>
      <Td>
        <div className="space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            {/* The name is the link. A separate "View" button in the action
                column would compete with Pause for the same glance, and the
                thing somebody wants to open is the person. */}
            <Link
              className="text-sm text-fg transition-colors duration-200 ease-smooth hover:text-accent"
              to={`/admin/affiliates/${row.id}`}
            >
              {row.displayName || "—"}
            </Link>
            {row.role !== "member" && <Tag>{t(`affiliates.role.${row.role}`)}</Tag>}
            {paused && <StatusBadge tone="warn" label={t(`affiliates.status.${row.status}`)} />}
          </div>
          <p className="text-[12px] text-fg-subtle">
            {t("affiliates.summary", { signups: formatCount(row.referrals), date: formatDate(row.joinedAt) })}
            {row.payoutAddress ? "" : t("affiliates.noWallet")}
          </p>
          {row.statusReason && (
            <p className="text-[12px] text-fg-subtle">{row.statusReason}</p>
          )}
        </div>
      </Td>
      <Td priority="lg" className="text-right tabular-nums">
        <Mono className="text-fg-muted">{row.links}</Mono>
      </Td>
      <Td priority="md" className="text-right tabular-nums">
        {formatCount(row.conversions)}
      </Td>
      <Td className="text-right tabular-nums">{formatMoney(row.earnedMicros)}</Td>
      {canManage && (
        <Td className="text-right">
          <div className="flex items-center justify-end gap-2">
            {/*
             * No confirmation dialog on either control. Both are reversible in
             * one move from this same row — a paused affiliate is reinstated, a
             * role is set back — and a modal guarding an undoable action is the
             * kind of friction that teaches people to dismiss modals.
             *
             * The one genuinely irreversible act in this section is approving a
             * payout, and that is the one the queue makes you read a row for.
             */}
            <Select
              value={row.role}
              disabled={busy}
              onChange={(event) => onRole(event.target.value as AffiliateRole)}
              className="h-8 w-32"
              aria-label={t("affiliates.roleFor", { name: row.displayName || t("affiliates.thisAffiliate") })}
            >
              <option value="member">{t("affiliates.role.member")}</option>
              <option value="finance">{t("affiliates.role.finance")}</option>
              <option value="admin">{t("affiliates.role.admin")}</option>
            </Select>
            {paused ? (
              <Button variant="secondary" size="sm" loading={busy} onClick={() => onStatus("active")}>
                {t("affiliates.reinstate")}
              </Button>
            ) : (
              <Button
                variant="danger"
                size="sm"
                loading={busy}
                onClick={() => onStatus("paused", t("affiliates.pausedReason"))}
              >
                {t("affiliates.pause")}
              </Button>
            )}
          </div>
        </Td>
      )}
    </tr>
  );
}
